import {useApp} from "../../app.context";
import {useEffect, useState} from "react";
import StringInputLabel from "../../elements/stringInputLabel.element";
import NumberInputLabel from "../../elements/numberInputLabel.element";
import {useIntegrator} from "../../integrator.context";
import {MetadataCollection, MetadataValueMethod, MetadataValueCommand, MetadataValueTypes} from "../../commands/metadataValue.command";
import SelectableLabel from "../../elements/selectable.label";

function AddMetadataValueView() {
    const {state, executeCommand} = useIntegrator();
    const {metadataValue, setMetadataValue, getEntityOptions, getAttributeManifestEntry} = useApp();
    const [key, setKey] = useState<string>('');
    const [type, setType] = useState<MetadataValueTypes>(MetadataValueTypes.Number);
    const [defaultNumber, setDefaultNumber] = useState<number>(0);
    const [defaultEntity, setDefaultEntity] = useState<string>('');

    const curType = type.toLowerCase();
    const isRequires = curType === MetadataValueTypes.Requires;

    const entityGroups = getEntityOptions();

    // When the target attribute is in the manifest, its parameters and entities
    // are schema-defined and edited inline — so the manual Number/Entity add
    // (which lets you invent arbitrary keys) is suppressed here. Requires and
    // EntityValue remain hand-authored.
    const manifestBacked = metadataValue
        ? !!getAttributeManifestEntry(metadataValue.metadata.id, metadataValue.collection)
        : false;

    // Requires only makes sense on unit attributes — that's the only place
    // the validator looks. Hide the option for other collections.
    const typeOptions = Object.keys(MetadataValueTypes).filter(t => {
        if (t === 'Requires') return metadataValue?.collection === MetadataCollection.Units;
        if (manifestBacked && (t === 'Number' || t === 'Entity')) return false;
        return true;
    });

    // Keep the selected type valid: if Number/Entity get suppressed for a
    // manifest-backed attribute, fall back to EntityValue (always offered).
    useEffect(() => {
        if (manifestBacked && (curType === MetadataValueTypes.Number || curType === MetadataValueTypes.Entity)) {
            setType(MetadataValueTypes.EntityValue);
        }
    }, [manifestBacked, curType]);

    // For Requires, the "key" is the required attribute id; offer a dropdown
    // of existing unit-attribute IDs.
    const requireOptions = Array.from(new Set(state.unitAttributes?.map(a => a.id) ?? []))
        .filter(id => id && id !== metadataValue?.metadata.id)
        .sort((a, b) => a.localeCompare(b));

    function addValue() {
        if (metadataValue === undefined || key.length === 0) {
            return;
        }

        const method = MetadataValueMethod.AddOrUpdate;
        const value = curType === MetadataValueTypes.Entity ? defaultEntity : defaultNumber;

        executeCommand(new MetadataValueCommand(metadataValue.collection, method, curType as MetadataValueTypes, metadataValue.metadata.uid, key, value))
        setMetadataValue(undefined);
    }

    return (
        <div className={'layout vertical overlay'} onClick={() => {
            setMetadataValue(undefined);
        }}>
            <div className={'layout vertical gap box'} style={{padding: 10, gap: 10, width: 400}}
                 onClick={e=> e.stopPropagation()}
            >
                <p className={'text header'}>New Attribute Value</p>
                {manifestBacked && <p className={'muted-text'} style={{fontSize: 13}}>
                    Parameters and entities for this attribute are defined by the manifest and edited inline.
                </p>}
                <SelectableLabel name={'Type'} value={type} options={typeOptions} literalOptions onChange={v => setType(v as MetadataValueTypes)}/>
                {curType === MetadataValueTypes.Number && <StringInputLabel title={'Key'} placeholder={'Enter key...'} value={key} onChange={setKey}/>}
                {curType === MetadataValueTypes.Entity && <StringInputLabel title={'Key'} placeholder={'Enter key...'} value={key} onChange={setKey}/>}
                {curType === MetadataValueTypes.EntityValue && <SelectableLabel name={'Key'} value={key} groups={entityGroups}
                             onChange={v => setKey(String(v))}
                />}
                {isRequires && <SelectableLabel name={'Required Attribute'} value={key} options={requireOptions}
                             onChange={v => setKey(v)}
                />}
                {(curType === MetadataValueTypes.Number || curType === MetadataValueTypes.EntityValue) && <NumberInputLabel title={'Default Value'} value={defaultNumber} onChange={setDefaultNumber}/>}
                {curType === MetadataValueTypes.Entity && <SelectableLabel name={'Default Value'} value={defaultEntity} groups={entityGroups}
                                                                    onChange={v => setDefaultEntity(String(v))}
                />}
                <button onClick={addValue}>Add</button>
            </div>
        </div>
    )
}

export default AddMetadataValueView;